import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Emblem, EmblemPicker } from "@/components/cast/Emblem";
import { Logo } from "@/components/cast/Layout";

const INTERESTS = [{ k: "devotion", l: "Daily devotion" }, { k: "study", l: "Bible study" }, { k: "stories", l: "Bible stories" }, { k: "testimony", l: "Testimonies" }];

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [sugs, setSugs] = useState<string[]>([]);
  const [f, setF] = useState<any>({ username: user.username || "", interests: user.interests || [], avatar: user.avatar || { icon: "anchor", color: "#1E293B" }, show_name: false, birth_date: "", is_parent: false });
  const [error, setError] = useState("");

  const load = () => api.get("/username/suggest").then((r) => { setSugs(r.data.suggestions); setF((p: any) => (p.username ? p : { ...p, username: r.data.suggestions[0] })); });
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggle = (k: string) => setF({ ...f, interests: f.interests.includes(k) ? f.interests.filter((x: string) => x !== k) : [...f.interests, k] });
  const submit = async () => {
    setError("");
    try {
      const { data } = await api.put("/me/profile", { ...f, birth_date: f.birth_date || null });
      setUser(data);
      nav(f.is_parent ? "/parent" : "/shore");
    } catch (e) { setError(errMsg(e)); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] px-5 py-10">
      <div className="mx-auto max-w-2xl">
        <Logo />
        <p className="eyebrow mt-12">Set up your profile</p>
        <h1 className="mt-3 font-display text-4xl font-semibold text-slate-900 sm:text-5xl">Choose how you'll appear</h1>
        <p className="mt-3 text-slate-600">On Cast you're known by a username, not your real name. Pick one below or write your own.</p>

        <div className="card-warm mt-10 space-y-8 p-6 sm:p-10">
          <div className="flex items-center gap-4">
            <Emblem avatar={f.avatar} size={64} />
            <div className="flex-1">
              <Label htmlFor="username">Username</Label>
              <Input id="username" data-testid="onboarding-username-input" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} className="mt-1.5 h-11 bg-white" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {sugs.map((s) => <button key={s} type="button" data-testid={`username-suggestion-${s}`} onClick={() => setF({ ...f, username: s })} className="chip transition-colors hover:bg-[#E2DBCE]">{s}</button>)}
            <Button type="button" variant="ghost" size="sm" onClick={load} data-testid="username-generate-button"><RefreshCw className="mr-1 h-3.5 w-3.5" />New ideas</Button>
          </div>
          <div><Label>Profile emblem</Label><div className="mt-3"><EmblemPicker value={f.avatar} onChange={(a) => setF({ ...f, avatar: a })} /></div></div>
          <div>
            <Label>What brings you here?</Label>
            <div className="mt-3 flex flex-wrap gap-2">
              {INTERESTS.map((i) => (
                <button key={i.k} type="button" data-testid={`interest-${i.k}`} onClick={() => toggle(i.k)}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${f.interests.includes(i.k) ? "border-[#1E293B] bg-[#1E293B] text-white" : "border-[#E2DBCE] bg-white text-slate-700 hover:bg-[#F3ECE0]"}`}>{i.l}</button>
              ))}
            </div>
          </div>
          {!user.birth_date && (
            <div><Label htmlFor="bd">Birth date (Cast accounts are 18+)</Label><Input id="bd" type="date" data-testid="onboarding-birthdate-input" value={f.birth_date} onChange={(e) => setF({ ...f, birth_date: e.target.value })} className="mt-1.5 h-11 bg-white" /></div>
          )}
          <div className="flex items-center justify-between gap-4 rounded-xl bg-[#F3ECE0]/60 p-4">
            <div><p className="font-semibold text-slate-800">Show my first name with my username</p><p className="text-sm text-slate-600">Off keeps you pseudonymous on the testimony feed.</p></div>
            <Switch data-testid="onboarding-show-name-switch" checked={f.show_name} onCheckedChange={(v) => setF({ ...f, show_name: v })} />
          </div>
          <label className="flex items-start gap-3">
            <Checkbox data-testid="onboarding-parent-checkbox" checked={f.is_parent} onCheckedChange={(v) => setF({ ...f, is_parent: !!v })} className="mt-0.5" />
            <span className="text-sm text-slate-700"><b>I'm a parent.</b> I'd like to set up child profiles (ages 5–15) for the Kids Cove learning area.</span>
          </label>
          {error && <p data-testid="onboarding-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          <Button onClick={submit} data-testid="onboarding-submit-button" className="h-12 w-full rounded-full bg-[#C86D51] text-base font-semibold text-white hover:bg-[#B35A3F]">Step onto the Shore</Button>
        </div>
      </div>
    </div>
  );
}
